import { useState, useEffect, useRef } from 'react';

interface ItemWithId {
  id: number;
}

interface UseExpandableItemsProps<T extends ItemWithId> {
  items: T[];
  expandFirstByDefault?: boolean;
}

export const useExpandableItems = <T extends ItemWithId>({
  items,
  expandFirstByDefault = true,
}: UseExpandableItemsProps<T>) => {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [expandedSections, setExpandedSections] = useState<
    Record<number, string[]>
  >({});
  const hasInitialExpanded = useRef(false);

  useEffect(() => {
    if (
      expandFirstByDefault &&
      items.length > 0 &&
      !hasInitialExpanded.current
    ) {
      setExpandedItems([items[0].id]);
      hasInitialExpanded.current = true;
    }
  }, [items, expandFirstByDefault]);

  const toggleItem = (itemId: number) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  const isItemExpanded = (itemId: number): boolean => {
    return expandedItems.includes(itemId);
  };

  const toggleSection = (itemId: number, section: string) => {
    setExpandedSections((prev) => {
      const current = prev[itemId] || [];
      const updated = current.includes(section)
        ? current.filter((s) => s !== section)
        : [...current, section];
      return { ...prev, [itemId]: updated };
    });
  };

  const getExpandedSections = (itemId: number): string[] => {
    return expandedSections[itemId] || [];
  };

  return {
    expandedItems,
    toggleItem,
    isItemExpanded,
    expandedSections,
    toggleSection,
    getExpandedSections,
  };
};
